import {
  GridCallbackDetails,
  GridFilterModel,
  GridPaginationModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { Link as RouterLink, useLocation, useNavigate, useOutletContext } from "react-router";
import { authorizedObjectOnPanel } from "./drive-center-panel";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
  DrivePanelState,
  GetPagination,
  SetPanelObject,
  SetPanelState,
} from "../../../Redux/Reducers/redux-slice-drive";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { routePaths } from "../../../Router/router";
import { use, useCallback, useContext, useState } from "react";
import DataTable from "../../Table/table";
import { useDriveTableCols } from "../helpers/use-drive-table-cols";
import { CHILDREN_PAGE_SIZE } from "../helpers/use-drive-pagination";
import TableUuids from "../../Table/table-uuids";
import { DriveContext } from "./drive-navigate-handler";

interface IDriveAssetTableProps {
  resultObjects: DbAssetObjectInfos[];
  loadContents: (
    paginationModel: GridPaginationModel,
    replace?: boolean,
    filterModel?: GridFilterModel,
    sort?: GridSortModel
  ) => Promise<void>;
}

export const DriveAssetTable = (props: IDriveAssetTableProps) => {
  const { resultObjects, loadContents } = props;
  const { totalChildrenCount, isNextPageLoading } = useAppSelector(GetPagination);
  const cols = useDriveTableCols();
  const driveContext = use(DriveContext);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: CHILDREN_PAGE_SIZE,
  });
  const [filterModelLocal, setFilterModelLocal] = useState<GridFilterModel>({
    items: [],
    quickFilterValues: [""],
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  type Row = (typeof resultObjects)[number];

  // Context
  const { selectedItems: selectedItems, setSelectedItems } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
    selectedItems: DbAssetObjectInfos[];
  }>();

  //   const handlePageChange: ((page: number, pageSize: number) => void) | undefined = useCallback(
  //     (page, pageSize) => {
  //       loadContents(page, pageSize, true);
  //     },
  //     [loadContents]
  //   );

  const handleSelectionModelChange = (newSelectedRows: GridRowSelectionModel) => {
    const selectedIds = newSelectedRows as number[];
    setSelectedItems(resultObjects.filter((x) => selectedIds.includes(x.id)));
  };

  const handlePaginationModelChange = useCallback(
    (paginationModel: GridPaginationModel, details: GridCallbackDetails<"pagination">) => {
      setPaginationModel(paginationModel);
      const sortModel = details.api.state.sorting.sortModel;
      const filterModel = details.api.state.filter.filterModel;
      loadContents(paginationModel, true, filterModel, details.api.state.sorting.sortModel);
    },
    []
  );

  const handleSortModelChange = (sortModel: GridSortModel, details: GridCallbackDetails) => {
    const paginationModel = details.api.state.pagination.paginationModel;
    const filterModel = details.api.state.filter.filterModel;
    loadContents(paginationModel, true, filterModel, sortModel);
  };

  const handleFilterChange = (filterModel: GridFilterModel, details: GridCallbackDetails) => {
    console.log(filterModel);
    const paginationModel = details.api.state.pagination.paginationModel;
    const sortModel = details.api.state.sorting.sortModel;
    // restrict server filter to quickFilterValues only
    if (filterModel.quickFilterValues !== filterModelLocal.quickFilterValues) {
      loadContents(paginationModel, true, filterModel, sortModel);
    }
    setFilterModelLocal(filterModel);
  };

  const apiGrid = useGridApiRef();

  return (
    <DataTable
      tableKey={TableUuids.DRIVE_ASSET_TABLE}
      rows={resultObjects}
      columns={cols}
      checkboxSelection
      pageSize={CHILDREN_PAGE_SIZE}
      disableRowSelectionOnClick
      //   parentPermsInherit={treeObj?.permsInherit}
      loading={isNextPageLoading}
      rowCount={totalChildrenCount}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      onRowSelectionModelChange={handleSelectionModelChange}
      sortingMode="server"
      onSortModelChange={handleSortModelChange}
      filterModel={filterModelLocal}
      onFilterModelChange={handleFilterChange}
      resetPageOnSortFilter
      pagination={true}
      selectedRows={selectedItems.map((x) => x.id)}
      onRowClick={(params: GridRowParams) => {
        if (selectedItems.length > 0) {
          if (selectedItems.indexOf(params.row) !== -1) {
            setSelectedItems((prev) => prev.filter((x) => x.id !== params.row.id));
            return;
          }
          setSelectedItems((prev) => [...prev, params.row]);
          return;
        }

        if (authorizedObjectOnPanel.includes(params?.row?.type)) {
          dispatch(SetPanelObject(params.row));
          dispatch(SetPanelState(DrivePanelState.Preview));
        }
      }}
      onRowDoubleClick={(params: GridRowParams) => {
        if (selectedItems.length > 0) {
          return;
        }
        const isFolder =
          params?.row?.type === ObjectType.Folder ||
          params?.row?.type === ObjectType.Group ||
          params?.row?.type === ObjectType.GraphSkeleton;
        if (isFolder) {
          navigate(driveContext?.basePath + "/" + params.row.id, {
            state: {
              back: {
                retrieveId: params?.row?.id,
                path: location.pathname,
                back: location.state?.back,
              },
            },
          });
          return;
        }

        if (authorizedObjectOnPanel.includes(params?.row?.type) && selectedItems.length === 0) {
          dispatch(SetPanelObject(params.row));
          dispatch(SetPanelState(DrivePanelState.Enlarged));
        }
      }}
      initialState={{
        pinnedColumns: { left: ["__check__", "name"], right: ["actions", "driveActions"] },
        sorting: { sortModel: [{ field: "name", sort: "desc" }] },
      }}
      apiRef={apiGrid}
    />
  );
};
